import api from '../lib/api';
import type { ApiResponse } from '@maios/shared';

export interface BugReportListItem {
  id: string;
  reportNumber: number;
  category: string;
  title: string;
  status: string;
  reporterEmail: string;
  createdAt: string;
  updatedAt: string;
  organizationName: string | null;
  memoCount: number;
}

export interface BugReportListResponse {
  items: BugReportListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface BugReportListParams {
  page?: number;
  limit?: number;
  status?: string;
  category?: string;
  organizationId?: string;
  search?: string;
}

export interface BugReportDetail {
  id: string;
  reportNumber: number;
  category: string;
  title: string;
  description: string;
  status: string;
  reporterEmail: string;
  pageUrl: string | null;
  userAgent: string | null;
  attachments: unknown;
  createdAt: string;
  updatedAt: string;
  organization: { id: string; name: string; slug: string } | null;
  user: { id: string; name: string; email: string } | null;
  memos: BugReportMemo[];
}

export interface BugReportMemo {
  id: string;
  content: string;
  createdAt: string;
  author: { id: string; name: string; email: string };
}

export async function getBugReports(params: BugReportListParams): Promise<BugReportListResponse> {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.status) query.set('status', params.status);
  if (params.category) query.set('category', params.category);
  if (params.organizationId) query.set('organizationId', params.organizationId);
  if (params.search) query.set('search', params.search);

  const res = await api.get<ApiResponse<BugReportListResponse>>(`/admin/bug-reports?${query}`);
  return res.data.data!;
}

export async function getBugReport(id: string): Promise<BugReportDetail> {
  const res = await api.get<ApiResponse<BugReportDetail>>(`/admin/bug-reports/${id}`);
  return res.data.data!;
}

export async function updateBugReportStatus(id: string, params: { status: string; sendNotification?: boolean }): Promise<void> {
  await api.patch(`/admin/bug-reports/${id}/status`, params);
}

export async function addMemo(id: string, content: string): Promise<BugReportMemo> {
  const res = await api.post<ApiResponse<BugReportMemo>>(`/admin/bug-reports/${id}/memo`, { content });
  return res.data.data!;
}

// --- 통계 ---

export interface BugReportStats {
  period: { from: string; to: string };
  categoryDistribution: Array<{ category: string; count: number }>;
  organizationRanking: Array<{ organizationId: string; organizationName: string; count: number }>;
  weeklyTrend: { labels: string[]; data: number[] };
  avgResolutionHours: number | null;
}

export async function getBugReportStats(days: number): Promise<BugReportStats> {
  const res = await api.get<ApiResponse<BugReportStats>>(`/admin/bug-reports/stats?days=${days}`);
  return res.data.data!;
}
